import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';

import { ToastrService } from 'ngx-toastr';
import { DashboardService } from '../../../Services/dashboard.service';
import { MasterInformationService } from '../../../Services/master-information.service';
import { apiEndPoints } from '../../../api/apiEndPoint';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-sales-grid-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPaginationModule],
  templateUrl: './sales-grid-dashboard.component.html',
  styleUrls: ['./sales-grid-dashboard.component.css'],
})
export class SalesGridDashboardComponent implements OnInit {
  cities: any = [];

  zones: any = [];

  years = [2024, 2025];
  ngFromDate: any;
  ngToDate: any;

  months = [
    { id: 1, name: 'January' },
    { id: 2, name: 'February' },
    { id: 3, name: 'March' },
    { id: 4, name: 'April' },
    { id: 5, name: 'May' },
    { id: 6, name: 'June' },
    { id: 7, name: 'July' },
    { id: 8, name: 'August' },
    { id: 9, name: 'September' },
    { id: 10, name: 'October' },
    { id: 11, name: 'November' },
    { id: 12, name: 'December' },
  ];

  // Filter and pagination variables
  searchQuery = '';
  selectedCityId = '';
  selectedZoneId = '';
  selectedYear = '';
  selectedMonth = '';
  currentPage = 1;
  itemsPerPage = 12;
  paginatedData: any;

  // ---------------------- Pagination ---------------------- //
  page = 1;
  pageSize = 5;

  // Filtered data
  filteredData: any[] = [];

  totalOrders = 0;
  sarReceivedFromRequestor = 0;
  sarAllocatedToDumpster = 0;
  sarAllocatedToDumpyard = 0;
  sarAllocatedToSmartSort = 0;
  sarRefundedToRequestor = 0;
  fromDate: any;
  isToDateShow: boolean = false;
  toDate: any;

  constructor(
    private dashBoardService: DashboardService,
    private masterService: MasterInformationService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.getCities();
    this.getFilteredRevenueData();
  }

  // Method to filter the grid
  searchData() {
    if (this.searchQuery == '') {
      this.selectedCityId = '';
      this.selectedZoneId = '';
      this.selectedYear = '';
      this.selectedMonth = '';
      this.getFilteredRevenueData();
      return;
    }

    this.filteredData = this.paginatedData.filter((item: any) => {
      return (
        (!this.selectedCityId || item.city === this.selectedCityId) &&
        (!this.selectedZoneId || item.zone === this.selectedZoneId) &&
        (!this.selectedYear || item.year === Number(this.selectedYear)) &&
        (!this.selectedMonth || item.month === this.selectedMonth) &&
        (!this.searchQuery ||
          Object.values(item).some((val: any) =>
            val
              .toString()
              .toLowerCase()
              .includes(this.searchQuery.toLowerCase())
          ))
      );
    });

    // Update the paginated data
    this.updatePaginatedData();
  }

  // Pagination handler
  onPageChange(page: number) {
    this.currentPage = page;
    this.updatePaginatedData();
  }

  // Update paginated data
  updatePaginatedData() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedData = this.filteredData.slice(startIndex, endIndex);
  }

  // Calculate totals for the filtered data (alternative method)
  calculateTotals() {
    this.totalOrders = this.filteredData.reduce(
      (sum, item) => sum + item.monthlyorderCount,
      0
    );
    this.sarReceivedFromRequestor = this.filteredData.reduce(
      (sum, item) => sum + item.sarReceivedFromReq,
      0
    );
    this.sarAllocatedToDumpster = this.filteredData.reduce(
      (sum, item) => sum + item.sarAllocatedToDumpster,
      0
    );
    this.sarAllocatedToDumpyard = this.filteredData.reduce(
      (sum, item) => sum + item.sarAllocatedToDumpyard,
      0
    );
    this.sarAllocatedToSmartSort = this.filteredData.reduce(
      (sum, item) => sum + item.sarAllocatedToSmartsort,
      0
    );
    this.sarRefundedToRequestor = this.filteredData.reduce(
      (sum, item) => sum + item.sarRefundToReq,
      0
    );
  }

  getCities() {
    this.masterService.getCityList(apiEndPoints.cityList).subscribe({
      next: (response: any) => {
        this.cities = response.body.details;
      },
      error: (error) => {
        this.toastr.error(
          'An unexpected error occurred. Please try again later.',
          'Error',
          {
            timeOut: 3000,
            positionClass: 'toast-top-right',
            progressBar: true,
          }
        );
      },
    });
  }

  getZones() {
    this.dashBoardService
      .getZoneList(apiEndPoints.zoneList, this.selectedCityId)
      .subscribe({
        next: (response: any) => {
          this.zones = response.body.details;
        },
        error: (error) => {
          this.toastr.error(
            'An unexpected error occurred. Please try again later.',
            'Error',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            }
          );
        },
      });
  }

  handleCityChange() {
    if (this.selectedCityId == '') {
      this.selectedZoneId = '';
    }
    this.currentPage = 1;
    this.getZones();
    this.getFilteredRevenueData();
  }

  handleZoneChange() {
    this.currentPage = 1;
    this.getFilteredRevenueData();
  }

  handleYearChange() {
    this.currentPage = 1;
    this.getFilteredRevenueData();
  }

  handleMonthChange() {
    this.currentPage = 1;
    this.getFilteredRevenueData();
  }

  getFilteredRevenueData() {
    this.dashBoardService
      .getFilteredRevenueData(
        apiEndPoints.revenueDashboardData,
        this.selectedCityId,
        this.selectedZoneId,
        this.selectedYear,
        this.fromDate,
        this.toDate
      )
      .subscribe({
        next: (res: any) => {
          this.paginatedData = res.body.details.monthlyData;
          this.filteredData = [...this.paginatedData];
          this.updatePaginatedData();
          this.calculateTotals();
        },

        error: (err) => {
          this.toastr.error(
            'An unexpected error occurred. Please try again later.',
            'Error',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            }
          );
        },
      });
  }

  // ----------------------------------- Date Filter ----------------------------------- //

  onFromDateChange(event: any) {
    this.ngToDate = [];
    this.fromDate = event.target.value;
    this.ngFromDate = this.fromDate;
    this.isToDateShow = true;
  }

  onToDateChange(event: any) {
    this.toDate = event.target.value;
    if (new Date(this.toDate) < new Date(this.fromDate)) {
      this.toastr.info('To Date cannot be before From Date!', 'Info', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
        progressBar: true,
      });
      this.ngToDate = '';
      this.toDate = '';
      return;
    }
    this.ngToDate = this.toDate;
    this.getFilteredRevenueData();
  }

  clearFilter() {
    this.fromDate = '';
    this.toDate = '';
    this.getFilteredRevenueData();
  }

  // ------------------------------- Export to Excel ------------------------------- //
  exportToExcel(): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.filteredData);
    const wb: XLSX.WorkBook = { Sheets: { data: ws }, SheetNames: ['data'] };
    XLSX.writeFile(wb, 'Monthly Revenue Report.xlsx');
  }

  exportToPDF() {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    const pdfFileName = 'Monthly-Revenue-Report.pdf';

    const logo = new Image();
    logo.src = 'assets/Images/CDM-01.png';
    logo.onload = () => {
      const logoWidth = 28;
      const logoHeight = 20;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 10;
      doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

      const arabicAddressImg = new Image();
      arabicAddressImg.src = 'assets/Images/arabic address.jpeg';
      arabicAddressImg.onload = () => {
        const arabicImgWidth = 70;
        const arabicImgHeight = 20;
        const arabicImgX = pageWidth - arabicImgWidth - 10;
        const arabicImgY = 10;
        doc.addImage(
          arabicAddressImg,
          'JPEG',
          arabicImgX,
          arabicImgY,
          arabicImgWidth,
          arabicImgHeight
        );

        // Add website below Arabic address image
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 255);
        doc.text(
          'www.smart­sort.net',
          arabicImgX + arabicImgWidth / 2,
          arabicImgY + arabicImgHeight + 5,
          { align: 'left' }
        );

        const englishAddressImg = new Image();
        englishAddressImg.src = 'assets/Images/english address.jpeg';
        englishAddressImg.onload = () => {
          const englishImgWidth = 70;
          const englishImgHeight = 20;
          const englishImgX = 10;
          const englishImgY = 10;
          doc.addImage(
            englishAddressImg,
            'JPEG',
            englishImgX,
            englishImgY,
            englishImgWidth,
            englishImgHeight
          );

          // Add website below English address image
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 255);
          doc.text(
            'www.smart­sort.net',
            englishImgX + englishImgWidth / 11,
            englishImgY + englishImgHeight + 5,
            { align: 'left' }
          );

          // Reset text color to black (optional, in case you add more text later)
          doc.setTextColor(0, 0, 0);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          const title = 'Monthly Revenue Report';
          const titleY = logoY + logoHeight + 10;
          doc.text(title, pageWidth / 2, titleY, { align: 'center' });
          // Table headers
          const headers = [
            [
              { content: 'Month' },
              { content: 'Monthly Order Count' },
              { content: 'SAR Received from Requestor' },
              { content: 'SAR Allocated to Dumpster' },
              { content: 'SAR Allocated to Dumpyard' },
              { content: 'SAR Allocated to SmartSort' },
              { content: 'SAR Refunded to Requestor' },
            ],
          ];

          let tableBody: any[] = [];

          // Ensure Year is filled for every row
          this.paginatedData.forEach((summary: any) => {
            tableBody.push([
              summary.month,
              summary.monthlyorderCount,
              summary.sarReceivedFromReq,
              summary.sarAllocatedToDumpster,
              summary.sarAllocatedToDumpyard,
              summary.sarAllocatedToSmartsort,
              summary.sarRefundToReq,
            ]);
          });

          // Add footer row with totals
          const footer = [
            'Total',
            this.totalOrders,
            this.sarReceivedFromRequestor,
            this.sarAllocatedToDumpster,
            this.sarAllocatedToDumpyard,
            this.sarAllocatedToSmartSort,
            this.sarRefundedToRequestor,
          ];
          tableBody.push(footer);

          // Generate table
          autoTable(doc, {
            startY: titleY + 10,
            head: headers,
            body: tableBody,
            styles: {
              fontSize: 8,
              cellPadding: 2,
              lineWidth: 0.3,
              lineColor: [50, 50, 50],
              halign: 'center',
            },
            theme: 'grid',
            headStyles: {
              fillColor: [176, 220, 101],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              lineColor: [50, 50, 50],
              lineWidth: 0.3,
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            columnStyles: { 0: { halign: 'center', fontStyle: 'bold' } },
            margin: { top: titleY + 20 },
            pageBreak: 'auto',
            rowPageBreak: 'avoid',
            didDrawPage: function (data) {
              const pageCount = doc.getNumberOfPages();
              doc.setFontSize(10);
              doc.text(
                `Page ${pageCount}`,
                pageWidth - 20,
                doc.internal.pageSize.height - 10
              );
            },
          });

          // Save the PDF
          doc.save(pdfFileName);
        };
      };
    };
  }
}
